"use client";

// THE WATER — the cells around a netti, as one piece that can stand beside ANY
// netti: the door's hero (AuthStage) or a person's own newborn in the birth's
// account moment (OnboardingBirth). It owns the field (cells.ts: seeds born
// beneath netti, carried round its flank by the turn, converting into orbs of
// its light) and the carrying cells (they open into a pane of light with a
// feathered edge; one or two at a time, never meeting).
//
// It does not own netti. The caller says where netti is (`locate`), in this
// layer's coordinates, and where the form's column ends; the water is measured
// on resize and re-asked twice a second, so a netti that is still settling
// into its seat is followed.
//
// `foretell` — the birth's cells are not yet the person's: each one carries a
// quiet "soon, in yours" under a hollow cell, what to expect rather than what
// is kept.

import { useEffect, useMemo, useRef, useState } from "react";
import { stageSequence } from "./stageCells";
import { createCellField } from "./cells";
import { CardFace, applyFeather } from "./StageCardFace";
import { CELL } from "../../lib/brand/cell";
import "./auth-stage.css";

export type WaterPlace = {
  /** netti's heart and the radius the cells flow around, in layer px */
  heart: { x: number; y: number; r: number };
  /** where the form's column ends (cells quieten left of it; cards stay right) */
  formRight: number;
  /** cells quieten below this y (0 = none) */
  quietBelow?: number;
};

type Sim = { x0: number; y: number; vy: number; amp: number; freq: number; ph: number };

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export default function StageWater({
  room,
  locate,
  dir,
  count,
  cards = true,
  ink,
  palette,
  foretell = false,
  stagger,
  orbScale,
  orbAlpha,
  className,
}: {
  room: "wide" | "narrow";
  /** where netti is, asked on resize and twice a second; null = not yet */
  locate: (layer: HTMLElement) => WaterPlace | null;
  /** −1 cells rise past netti · +1 they fall away beneath it */
  dir: 1 | -1;
  count: number;
  /** the carrying cells (off where there is no room to read them) */
  cards?: boolean;
  /** a pale ground: seeds and orbs in netti's own colours (cells.ts `ink`) */
  ink?: boolean;
  palette?: [number, number, number][];
  foretell?: boolean;
  /** seconds between the first cells appearing (revealed one by one) */
  stagger?: number;
  /** quieter light: the orbs smaller and fainter (cells.ts) — default 1 · 1 */
  orbScale?: number;
  orbAlpha?: number;
  className?: string;
}) {
  const wide = room === "wide";
  const layerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cardEls = useRef<(HTMLDivElement | null)[]>([]);
  const locateRef = useRef(locate);
  useEffect(() => { locateRef.current = locate; }, [locate]);

  const seq = useMemo(() => stageSequence(), []);
  // ONE OR TWO OPEN AT A TIME: three cells on a desktop, two on a phone, evenly
  // spaced along one path at one steady pace, so the spacing never changes and
  // they can never meet
  const nCards = cards ? (wide ? 3 : 2) : 0;
  const [slots, setSlots] = useState<number[]>([0, 1, 2]);
  const nextItem = useRef(3);

  // THE FEATHER — one soft-edged cell image for every carrying cell's mask
  useEffect(() => {
    if (layerRef.current) applyFeather(layerRef.current);
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    const canvas = canvasRef.current;
    if (!layer || !canvas) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const field = createCellField(canvas, { count, dir, ink, palette, stagger, orbScale, orbAlpha });

    let W = 0, H = 0, L = 0; // layer size; L = where the form's column ends
    let heartY = 0, heartR = 0, nettiLeft = 0;
    const cardW = () => (wide ? 336 : Math.min(300, W * 0.76));
    const cardH = () => cardW() / 1.29;
    const lanes = () => {
      if (!wide) return [W / 2, W / 2];
      // midway between the form and netti — but never over the form
      const mid = Math.max(L + cardW() / 2 + 40, (L + nettiLeft) / 2);
      return [mid - 28, mid + 28];
    };

    /** where netti is — cheap, so it can be asked while netti settles */
    const relocate = () => {
      const place = locateRef.current(layer);
      if (!place) return false;
      heartY = place.heart.y;
      heartR = place.heart.r;
      nettiLeft = place.heart.x - place.heart.r;
      L = place.formRight;
      field.setHeart(place.heart.x, place.heart.y, place.heart.r);
      field.setQuiet(L);
      field.setQuietBelow(place.quietBelow ?? 0);
      return true;
    };
    const measure = () => {
      W = layer.clientWidth;
      H = layer.clientHeight;
      if (!W || !H) return;
      layer.style.setProperty("--stc-w", `${cardW().toFixed(0)}px`);
      relocate();
      field.resize(W, H); // after the heart is known, so the first scatter avoids it
    };
    measure();
    const follow = window.setInterval(relocate, 500);

    // A carrying cell's journey: a path from y0 to y1 at one steady pace —
    // rising, bottom → top through the open water; falling, out from behind
    // netti's heart → the foot of the room. The cells sit an equal share of
    // the path apart and a cell that finishes carries its overshoot back to
    // the start, so the spacing holds forever.
    const sims: Sim[] = [];
    const open: boolean[] = [];
    const DUR = wide ? 22 : 21; // seconds for the whole path (~9-11s of it open)
    const ends = () => {
      const pad = cardH() / 2 + 20;
      return dir < 0 ? [H + pad, -pad] : [heartY + heartR * 0.2, H + pad];
    };
    const spawnCard = (i: number, p: number) => {
      const [y0, y1] = ends();
      sims[i] = {
        x0: lanes()[i % 2] + rand(-12, 12),
        y: y0 + (y1 - y0) * p,
        vy: (y1 - y0) / DUR,
        amp: rand(8, 16),
        freq: rand(0.3, 0.5),
        ph: rand(0, Math.PI * 2),
      };
    };
    // the first is open as the room arrives; the rest follow at equal shares
    for (let i = 0; i < nCards; i++) {
      spawnCard(i, (((dir < 0 ? 0.45 : 0.3) - i / nCards) % 1 + 1) % 1);
      open[i] = false;
    }

    const ro = new ResizeObserver(() => {
      const prevH = H;
      measure();
      if (!prevH || !H) return;
      for (let i = 0; i < nCards; i++) { sims[i].x0 = lanes()[i % 2]; sims[i].y *= H / prevH; }
    });
    ro.observe(layer);

    let visible = true;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 });
    io.observe(layer);

    const paint = (t: number, dt: number) => {
      field.frame(t, dt);
      const cw = cardW(), ch = cardH();
      const [y0, y1] = ends();
      for (let i = 0; i < nCards; i++) {
        const el = cardEls.current[i];
        const s = sims[i];
        if (!el || !s) continue;
        s.y += s.vy * dt;
        let p = (s.y - y0) / (y1 - y0); // 0 → 1 along its path
        if (p > 1) {
          spawnCard(i, p - 1);
          p -= 1;
          const next = nextItem.current++ % seq.length;
          setSlots((prev) => { const out = prev.slice(); out[i] = next; return out; });
        }
        const x = s.x0 + Math.sin(t * s.freq + s.ph) * s.amp;
        el.style.transform = `translate3d(${(x - cw / 2).toFixed(1)}px, ${(s.y - ch / 2).toFixed(1)}px, 0)`;
        el.style.opacity = (Math.min(1, p / 0.07) * Math.min(1, (1 - p) / 0.06)).toFixed(3);
        // open while it is wholly in the room: rising, clear of the top and the
        // bottom · falling, below netti's heart and above the room's last line
        const isOpen = dir < 0
          ? s.y > ch / 2 + 28 && s.y < H - ch / 2 - 28
          : s.y > heartY + 36 + ch / 2 && s.y < H * 0.95 - ch / 2;
        if (isOpen !== open[i]) { open[i] = isOpen; el.classList.toggle("is-open", isOpen); }
      }
    };

    if (reduce) {
      paint(0, 0);
      return () => { window.clearInterval(follow); ro.disconnect(); io.disconnect(); field.dispose(); };
    }

    let raf = 0;
    let prev = performance.now();
    const t0 = prev;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, Math.max(0, (now - prev) / 1000));
      prev = now;
      if (!visible || document.hidden) return;
      paint((now - t0) / 1000, dt);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(follow);
      ro.disconnect();
      io.disconnect();
      field.dispose();
    };
    // the palette is read once per mount (a netti's colours do not change in
    // place); slot swaps live in refs + state the loop never reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wide, nCards, dir, count, ink, orbScale, orbAlpha]);

  return (
    <div
      ref={layerRef}
      className={["auth-stage__water", "stage-water", ink ? "stage-water--ink" : "", className ?? ""].filter(Boolean).join(" ")}
      data-room={room}
      aria-hidden
    >
      <canvas ref={canvasRef} className="auth-stage__bubbles" />
      {Array.from({ length: nCards }, (_, i) => {
        const c = seq[slots[i] % seq.length];
        return (
          <div key={`c${i}`} ref={(el) => { cardEls.current[i] = el; }} className="stc" style={{ opacity: 0 }}>
            <div className="stc-glow" />
            <div className="stc-cell">
              <div className={`stc-face stc-face--${c.kind}`}>
                <CardFace c={c} />
                {foretell ? (
                  <span className="stc-soon">
                    {/* a HOLLOW cell: not filled yet */}
                    <svg viewBox={CELL.viewBox} aria-hidden>
                      <path d={CELL.path} fill="none" stroke="currentColor" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
                    </svg>
                    soon, in yours
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
